import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Paper, TextField, Stack } from '@mui/material';
import Loading from '../../../components/loading';
import SuccessMessage from '../../../components/successMessage';
import ErrorMessage from '../../../components/errorMessage';
import { useAnamnesisV2 } from './hooks/useAnamnesisV2';
import { useSaveAnamnesisV2 } from './hooks/useSaveAnamnesisV2';

export default function AnamnesisV2FormMobile() {
  const navigate = useNavigate();
  const { patientId } = useParams();

  const { fetchByPatient, fetchQuestions } = useAnamnesisV2();
  const { save: saveAnamnesisV2Request } = useSaveAnamnesisV2();

  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [questionsWithAnswers, setQuestionsWithAnswers] = useState([]);
  const [mainComplaint, setMainComplaint] = useState('');
  const [anamnesisId, setAnamnesisId] = useState(0);
  const [visibleQuestions, setVisibleQuestions] = useState(3);
  const showMoreRef = useRef(null);

  const fetchPatientAnamnesis = useCallback(() => {
    if (!patientId) return;
    setLoading(true);
    fetchByPatient(patientId)
      .then((res) => {
        if (res.data) {
          setAnamnesisId(res.data.id);
          setMainComplaint(res.data.mainComplaint);
          const questions = res.data.questions.map((q) => ({
            ...q,
            answer: q.answer ?? '',
          }));
          setQuestionsWithAnswers(questions);
        } else {
          fetchCatalogQuestions();
        }
      })
      .catch((err) => {
        setShowErrorMessage(true);
        setErrorMessage(err.response?.data ?? 'Erro ao carregar anamnese.');
        if (err.response?.status === 401) navigate('/expired-session');
        console.error('ops! ocorreu um erro' + err);
      })
      .finally(() => setLoading(false));
  }, [patientId, navigate, fetchByPatient]);

  useEffect(() => {
    fetchPatientAnamnesis();
  }, [fetchPatientAnamnesis]);

  const fetchCatalogQuestions = () => {
    setLoading(true);
    fetchQuestions()
      .then((res) => {
        setQuestionsWithAnswers(res.data.map((q) => ({ ...q, answer: '' })));
      })
      .catch((err) => {
        console.error('ops! ocorreu um erro' + err);
      })
      .finally(() => setLoading(false));
  };

  const updateAnswer = (value, questionWithAnswer) => {
    setQuestionsWithAnswers((prev) =>
      prev.map((q) => (q.id === questionWithAnswer.id ? { ...q, answer: value } : q)),
    );
  };

  const saveAnamnesis = (event) => {
    event.preventDefault();

    const anamnesis = {
      id: anamnesisId,
      mainComplaint,
      questions: questionsWithAnswers.filter((q) => q.answer?.trim()),
    };

    setLoading(true);

    saveAnamnesisV2Request(patientId, anamnesisId, anamnesis)
      .then((returnFromApi) => {
        setLoading(false);
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) navigate('/expired-session');
      });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box component="form" onSubmit={saveAnamnesis}>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <TextField
            data-cy="anamnesis-description"
            fullWidth
            label="Queixa Principal"
            value={mainComplaint}
            onChange={(e) => setMainComplaint(e.target.value)}
            variant="outlined"
            multiline
            rows={3}
          />
        </Paper>

        {questionsWithAnswers.slice(0, visibleQuestions).map((q) => (
          <Paper
            data-cy="question-item"
            key={q.id}
            variant="outlined"
            sx={{ p: 2, borderRadius: 2 }}
          >
            <TextField
              fullWidth
              label={q.question}
              value={q.answer}
              onChange={(e) => updateAnswer(e.target.value, q)}
              variant="outlined"
              multiline
              rows={2}
            />
          </Paper>
        ))}

        {visibleQuestions < questionsWithAnswers.length && (
          <Button
            ref={showMoreRef}
            data-cy="show-more-questions-btn"
            variant="outlined"
            onClick={() => {
              setVisibleQuestions((prev) => prev + 4);
              setTimeout(
                () => showMoreRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
                50,
              );
            }}
          >
            Ver mais...
          </Button>
        )}

        <Button
          data-cy="save-anamnesis-btn"
          type="submit"
          variant="contained"
          fullWidth
          sx={{ p: 1.5 }}
        >
          Salvar
        </Button>
      </Stack>
    </Box>
  );
}
